import { useEffect, useState } from 'react';
import { errorText, locationLabel } from '../api';
import { formatDateTimeShort, formatTime } from '../format';
import { adminApi, type Booking, type BookingRequest } from './api';

/**
 * Übersicht der Buchungen: kommende und vergangene, Absage mit kurzer Nachricht an den Kunden, Meet-Link und
 * Markierung „noch ohne Meet-Link“ (Konzept 5.2), dazu die Terminwünsche aus Zeiten, in denen Google nicht
 * antwortete (Konzept 5.4) und die offenen Anfragen (Bestätigung durch den Nutzer, Spam-Schutz Stufe 2):
 * Annehmen macht daraus eine Buchung, Ablehnen schickt dem Kunden eine kurze Nachricht.
 */
export function BookingsPage() {
  const [scope, setScope] = useState<'upcoming' | 'past'>('upcoming');
  const [bookings, setBookings] = useState<Booking[] | null>(null);
  const [requests, setRequests] = useState<BookingRequest[]>([]);
  const [pending, setPending] = useState<Booking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<Booking | null>(null);
  const [declining, setDeclining] = useState<Booking | null>(null);
  const [accepting, setAccepting] = useState<string | null>(null);
  // Der Link aus dem Google-Termin oder aus einer Mail zeigt auf eine Buchung: ?buchung=<id>, bei Anfragen dazu &aktion=annehmen|ablehnen
  const [highlight] = useState(() => new URLSearchParams(window.location.search).get('buchung'));
  const [action] = useState(() => new URLSearchParams(window.location.search).get('aktion'));

  useEffect(() => {
    setBookings(null);
    adminApi.listBookings(scope).then((r) => setBookings(r.bookings)).catch((e) => setError(errorText(e)));
  }, [scope]);

  useEffect(() => {
    adminApi.listBookingRequests().then((r) => setRequests(r.requests)).catch(() => undefined);
    adminApi.listBookings('pending').then((r) => setPending(r.bookings)).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!highlight || !bookings) return;
    document.getElementById(`buchung-${highlight}`)?.scrollIntoView({ block: 'center' });
  }, [highlight, bookings]);

  // Aus der Mail „Ablehnen“: der Dialog öffnet sich von selbst; „Annehmen“ braucht trotzdem den Klick hier.
  useEffect(() => {
    if (action !== 'ablehnen' || !highlight) return;
    const found = pending.find((b) => b.id === highlight);
    if (found) setDeclining(found);
  }, [action, highlight, pending]);

  async function accept(b: Booking) {
    setAccepting(b.id);
    setError(null);
    try {
      const updated = await adminApi.acceptBooking(b.id);
      setPending(pending.filter((p) => p.id !== b.id));
      if (scope === 'upcoming') setBookings([...(bookings ?? []), updated].sort((x, y) => x.startsAt.localeCompare(y.startsAt)));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setAccepting(null);
    }
  }

  async function closeRequest(id: string) {
    try {
      const updated = await adminApi.closeBookingRequest(id);
      setRequests(requests.map((r) => (r.id === id ? updated : r)));
    } catch (e) {
      setError(errorText(e));
    }
  }

  const openRequests = requests.filter((r) => r.status !== 'closed');

  return (
    <>
      <div className="admin-head">
        <div>
          <h1>Buchungen</h1>
          <p className="muted">Wer wann kommt – und was du absagen musst.</p>
        </div>
        <div className="segmented">
          <button type="button" className={scope === 'upcoming' ? 'is-active' : ''} onClick={() => setScope('upcoming')}>
            Kommende
          </button>
          <button type="button" className={scope === 'past' ? 'is-active' : ''} onClick={() => setScope('past')}>
            Vergangene
          </button>
        </div>
      </div>
      {error && <div className="error">{error}</div>}

      {pending.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 className="card__title">Anfragen</h2>
          <p className="muted small">Diese Buchungen warten auf dein Ja. Die Zeit ist schon reserviert; mit „Annehmen“ landet der Termin im Kalender und der Kunde bekommt die Bestätigung. „Ablehnen“ schickt ihm deine kurze Nachricht und gibt die Zeit frei.</p>
          <div className="stack">
            {pending.map((b) => (
              <div key={b.id} id={`buchung-${b.id}`} className={`card booking booking--pending${b.id === highlight ? ' booking--highlight' : ''}`}>
                <div className="booking__when">
                  <strong>{formatDateTimeShort(b.startsAt)}</strong>
                  <span className="muted small">bis {formatTime(b.endsAt)} Uhr</span>
                  <span className="tag tag--warn">Anfrage</span>
                </div>
                <div className="booking__who">
                  <strong>{b.name}</strong>
                  {b.company && <span className="muted"> · {b.company}</span>}
                  <div className="small">
                    <a href={`mailto:${b.email}`}>{b.email}</a>
                    {b.phone && <> · {b.phone}</>}
                    {b.emailVerifiedAt && <span className="faint"> · E-Mail bestätigt</span>}
                  </div>
                  {b.message && <p className="small muted booking__message">„{b.message}"</p>}
                </div>
                <div className="booking__what">
                  <span className="small">
                    {b.eventType.title} · {locationLabel(b.eventType.location)}
                  </span>
                  <span className="faint small">{b.brand.name}</span>
                  <div className="save-bar">
                    <button type="button" className="button button--small" disabled={accepting === b.id} onClick={() => void accept(b)}>
                      {accepting === b.id ? 'Einen Moment …' : 'Annehmen'}
                    </button>
                    <button type="button" className="link-button" disabled={accepting === b.id} onClick={() => setDeclining(b)}>
                      Ablehnen
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {openRequests.length > 0 && (
        <div className="card" style={{ marginBottom: 20 }}>
          <h2 className="card__title">Terminwünsche</h2>
          <p className="muted small">Diese Leute wollten buchen, während Google nicht antwortete. Sobald die Terminauswahl wieder da ist, bekommen sie von selbst eine Mail mit dem Link – du musst nichts tun. „Erledigt“ nimmt den Eintrag aus der Liste.</p>
          <ul className="plain-list plain-list--rows">
            {openRequests.map((r) => (
              <li key={r.id}>
                <div>
                  <strong>{r.name}</strong> <a href={`mailto:${r.email}`}>{r.email}</a>
                  <div className="small muted">
                    {r.eventType.title} · {r.brand.name} · {formatDateTimeShort(r.createdAt)}
                    {r.status === 'notified' && r.notifiedAt && <> · Link geschickt {formatDateTimeShort(r.notifiedAt)}</>}
                    {r.status === 'open' && <> · wartet, bis Google wieder antwortet</>}
                  </div>
                </div>
                <div>
                  {r.status === 'open' ? <span className="tag tag--warn">wartet</span> : <span className="tag tag--ok">Link geschickt</span>}{' '}
                  <button type="button" className="link-button" onClick={() => void closeRequest(r.id)}>
                    Erledigt
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {bookings === null && !error && <p className="muted">Lade …</p>}
      {bookings?.length === 0 && <p className="muted">{scope === 'upcoming' ? 'Noch nichts gebucht.' : 'Noch keine vergangenen Termine.'}</p>}
      <div className="stack">
        {bookings?.map((b) => (
          <div key={b.id} id={`buchung-${b.id}`} className={`card booking${b.status === 'cancelled' ? ' booking--cancelled' : ''}${b.id === highlight ? ' booking--highlight' : ''}`}>
            <div className="booking__when">
              <strong>{formatDateTimeShort(b.startsAt)}</strong>
              <span className="muted small">bis {formatTime(b.endsAt)} Uhr</span>
              {b.status === 'cancelled' && <span className="tag tag--blocked">abgesagt</span>}
              {b.status === 'declined' && <span className="tag tag--blocked">abgelehnt</span>}
              {b.status === 'expired' && <span className="tag tag--muted">verfallen</span>}
              {b.status === 'pending' && <span className="tag tag--warn">Anfrage</span>}
              {b.status === 'confirmed' && b.eventType.location === 'meet' && b.googleSyncStatus && b.googleSyncStatus !== 'synced' && <span className="tag tag--warn">noch ohne Meet-Link</span>}
            </div>
            <div className="booking__who">
              <strong>{b.name}</strong>
              {b.company && <span className="muted"> · {b.company}</span>}
              <div className="small">
                <a href={`mailto:${b.email}`}>{b.email}</a>
                {b.phone && <> · {b.phone}</>}
                {b.emailVerifiedAt && <span className="faint"> · E-Mail bestätigt</span>}
              </div>
              {b.message && <p className="small muted booking__message">„{b.message}"</p>}
              {b.cancelReason && (
                <p className="small muted">
                  {b.status === 'declined' ? 'Abgelehnt von dir' : b.cancelledBy === 'host' ? 'Absage von dir' : 'Absage durch den Kunden'}: „{b.cancelReason}"
                </p>
              )}
            </div>
            <div className="booking__what">
              <span className="small">
                {b.eventType.title} · {locationLabel(b.eventType.location)}
              </span>
              <span className="faint small">{b.brand.name}</span>
              {b.meetUrl && b.status === 'confirmed' && (
                <a className="small" href={b.meetUrl} target="_blank" rel="noreferrer">
                  Zum Gespräch (Meet)
                </a>
              )}
              {b.status === 'confirmed' && b.googleSyncStatus === 'failed' && b.googleSyncError && <span className="faint small">Google: {b.googleSyncError}</span>}
              {b.status === 'confirmed' && scope === 'upcoming' && (
                <button type="button" className="link-button" onClick={() => setCancelling(b)}>
                  Absagen
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {cancelling && (
        <CancelDialog
          booking={cancelling}
          onClose={() => setCancelling(null)}
          onDone={(updated) => {
            setBookings((bookings ?? []).map((b) => (b.id === updated.id ? updated : b)));
            setCancelling(null);
          }}
        />
      )}
      {declining && (
        <DeclineDialog
          booking={declining}
          onClose={() => setDeclining(null)}
          onDone={(updated) => {
            setPending(pending.filter((b) => b.id !== updated.id));
            setDeclining(null);
          }}
        />
      )}
    </>
  );
}

function DeclineDialog({ booking, onClose, onDone }: { booking: Booking; onClose: () => void; onDone: (b: Booking) => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      onDone(await adminApi.declineBooking(booking.id, reason));
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  }

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="card dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="card__title">Anfrage ablehnen</h2>
        <p>
          {booking.name}, {formatDateTimeShort(booking.startsAt)} Uhr – {booking.eventType.title}
        </p>
        <div className="field">
          <label htmlFor="decline-reason">Kurze Nachricht an {booking.name.split(' ')[0]}</label>
          <textarea id="decline-reason" maxLength={1000} placeholder="z. B. Die Zeit passt bei mir leider nicht – magst du dir eine andere aussuchen?" value={reason} onChange={(e) => setReason(e.target.value)} />
          <span className="muted small">Die Mail geht sofort raus, mit dem Link zur Buchungsseite. Die Zeit ist danach wieder frei.</span>
        </div>
        {error && <div className="error">{error}</div>}
        <div className="save-bar">
          <button type="button" className="button button--danger button--small" disabled={busy} onClick={() => void confirm()}>
            Ablehnen
          </button>
          <button type="button" className="link-button" disabled={busy} onClick={onClose}>
            Doch nicht
          </button>
        </div>
      </div>
    </div>
  );
}

function CancelDialog({ booking, onClose, onDone }: { booking: Booking; onClose: () => void; onDone: (b: Booking) => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      onDone(await adminApi.cancelBooking(booking.id, reason));
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  }

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="card dialog" onClick={(e) => e.stopPropagation()}>
        <h2 className="card__title">Termin absagen</h2>
        <p>
          {booking.name}, {formatDateTimeShort(booking.startsAt)} Uhr – {booking.eventType.title}
        </p>
        <div className="field">
          <label htmlFor="cancel-reason">Kurze Nachricht an {booking.name.split(' ')[0]}</label>
          <textarea id="cancel-reason" maxLength={1000} placeholder="z. B. Mir ist etwas dazwischengekommen – magst du dir einen neuen Termin aussuchen?" value={reason} onChange={(e) => setReason(e.target.value)} />
          <span className="muted small">Die Mail geht sofort raus, mit einem Link für einen neuen Termin. Der Google-Termin verschwindet aus dem Kalender.</span>
        </div>
        {error && <div className="error">{error}</div>}
        <div className="save-bar">
          <button type="button" className="button button--danger button--small" disabled={busy} onClick={() => void confirm()}>
            Absagen
          </button>
          <button type="button" className="link-button" disabled={busy} onClick={onClose}>
            Doch nicht
          </button>
        </div>
      </div>
    </div>
  );
}
